// @ts-nocheck
import { getCookie, setCookie } from "@insite/client-framework/Common/Cookies";
import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import useGetSetting from "@insite/client-framework/Common/Hooks/useGetSetting";
import siteMessage from "@insite/client-framework/SiteMessage";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import translate from "@insite/client-framework/Translate";
import Button, { ButtonIcon, ButtonPresentationProps } from "@insite/mobius/Button";
import Drawer, { DrawerPresentationProps } from "@insite/mobius/Drawer";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import X from "@insite/mobius/Icons/X";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import React, { useEffect, useState } from "react";
import { css } from "styled-components";

/* Starter Custom Code - Start */
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { typographyInvertColorStyles } from "@store/Themes/common/mixins/typography";
import breakpoint from "@toolkit/Framework/Common/Utilities/breakpoint";
import breakpointValues from "@toolkit/Framework/Common/Utilities/breakpointValues";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
/* Starter Custom Code - End */

// Foundation --->
import constants from "@store/constants";
import { contentSpacingStyles } from "@store/Themes/common/mixins/spacing";
import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { breakpointMediaQueries, getProp } from "@insite/mobius/utilities";
import { mediaOptions } from "@insite/mobius/utilities/breakpointMediaQueries";
import { transitionHoverStyles } from "@store/Themes/common/mixins/transition";
import parse from "html-react-parser";
import { useSelector } from "react-redux";
// <--- Foundation

// Foundation --->
interface Props {
    content?: string;
    cookieTitle: string;
}
// <--- Foundation

export interface CookiePrivacyPolicyPopupStyles {
    drawer?: DrawerPresentationProps;
    container?: GridContainerProps;
    messageGridItem?: GridItemProps;
    messageText?: TypographyPresentationProps;
    acceptGridItem?: GridItemProps;
    acceptButton?: ButtonPresentationProps;
    closeGridItem?: GridItemProps;
    closeButton?: ButtonPresentationProps;
    // Starter Custom Code
    actionWrapper?: InjectableCss;
    closeLink?: LinkPresentationProps;
    // Foundation
    wrapper?: InjectableCss;
    textWrapper?: InjectableCss;
    actionsWrapper?: InjectableCss;
    cookieTitle?: TypographyPresentationProps;
    link?: LinkPresentationProps;
}

// Foundation
const buttonGlobalStyle: ButtonPresentationProps = {
    shape: "pill",
    hoverMode: "darken",
    typographyProps: {
        css: css`
            ${({ theme }) => `
                font-size: ${theme.design.typography.size.smallest};
                font-weight: ${theme.design.typography.weight.base};
            `}
        `,
    },
    css: css`
        align-self: center;
        padding: ${pxToRem(15)} ${pxToRem(30)};
        border-radius: ${pxToRem(25)};
        border: none;
    `,
    hoverStyle: {},
};
// Foundation

export const cookiePrivacyPolicyPopupStyles: CookiePrivacyPolicyPopupStyles = {
    drawer: {
        position: "bottom",
        size: 325,
        cssOverrides: {
            scrim: css`
                box-shadow: ${getProp("theme.shadows.alt")};

                ${({ theme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            css`
                                width: 100%;
                                margin: 0;
                            `,
                            css`
                                width: ${pxToRem(400)};
                                margin: ${pxToRem(15)};
                            `,
                        ],
                        mediaOptions.min,
                    )};
            `,
            drawerTitle: css`
                display: none;
            `,
            drawerContent: css`
                margin: 0 auto;
                width: 100%;
                height: 100%;
            `,
        },
        closeButtonProps: {
            css: css`
                display: none;
            `,
        },
        enableClickThrough: true,
    },
    container: {
        gap: 10,
        css: css`
            margin: 0 auto;
        `,
    },
    messageGridItem: {
        width: [12, 12, 7, 9, 9],
    },
    acceptGridItem: {
        width: [8, 9, 3, 2, 2],
    },
    acceptButton: buttonGlobalStyle,
    closeGridItem: {
        width: [4, 3, 2, 1, 1],
    },
    closeButton: {
        ...buttonGlobalStyle,
        color: "text.darkest",
    },
    wrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            width: 100%;
            height: 100%;
            padding: ${pxToRem(80)} ${pxToRem(41)};
            background-color: ${getColor("common.background")};
        `,
    },
    textWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            width: 100%;
            padding-bottom: ${pxToRem(30)};
        `,
    },
    actionsWrapper: {
        css: css`
            display: flex;
            width: 100%;
            margin: ${pxToRem(8)} 0 0;

            > * {
                ${contentSpacingStyles(0, pxToRem(10), 0)}
            }
        `,
    },
    cookieTitle: {
        css: css`
            margin: 0 0 ${pxToRem(5)};
            ${({ theme }) => `
                font-size: ${theme.design.typography.size.base};
                font-weight: ${theme.design.typography.weight.bold};
            `}
        `,
    },
    messageText: {
        css: css`
            ${({ theme }) => `
                font-size: ${theme.design.typography.size.small};
                line-height: ${theme.design.typography.lineHeight.small};
            `}
        `,
    },
    link: {
        typographyProps: {
            css: css`
                ${({ theme }) => `
                    font-size: ${theme.design.typography.size.small};
                    weight: ${theme.design.typography.weight.bold};
                `}
            `,
        },
        css: css`
            color: ${getColor("secondary")};

            &:hover {
                color: ${getColor("primary")};
                text-decoration: none;
                transition: ${transitionHoverStyles("color")};
            }
        `,
    },
};

const styles = cookiePrivacyPolicyPopupStyles;

const CookiePrivacyPolicyPopup = ({ content = "", cookieTitle /* Foundation */ }: Props) => {
    const location = useAppSelector(o => getLocation(o));
    const enableCookiePrivacyPolicyPopup = useGetSetting(o => o.websiteSettings.enableCookiePrivacyPolicyPopup);
    const [isOpen, setIsOpen] = useState(false);
    // Foundation --->
    const cookieName =
        useSelector((state: ApplicationState) => {
            const noticePopupCookieName = getSettingsCollection(state)?.bunzlSettings.noticePopupCookieName;

            if (noticePopupCookieName !== "") {
                return noticePopupCookieName;
            }
        }) || constants.cookieNames.viewedCookiePolicy;
    // <--- Foundation

    useEffect(() => {
        if (enableCookiePrivacyPolicyPopup && !getCookie(cookieName) /* Foundation */) {
            setIsOpen(true);
        }
    }, [location.pathname]);

    const acceptHandler = () => {
        setIsOpen(false);
        setCookie(cookieName, "yes", { expires: 365 }); // Foundation
    };

    const closeHandler = () => {
        setIsOpen(false);
    };

    return (
        <Drawer isOpen={isOpen} {...styles.drawer}>
            {/* Foundation ---> */}
            <StyledWrapper {...styles.wrapper}>
                <StyledWrapper {...styles.textWrapper}>
                    <Typography {...styles.cookieTitle}>{cookieTitle}</Typography>
                    <Typography {...styles.messageText}>{parse(content, parserOptions)}</Typography>
                </StyledWrapper>
                <StyledWrapper {...styles.actionsWrapper}>
                    <Button {...styles.acceptButton} onClick={acceptHandler}>
                        {siteMessage("Core_CookiePrivacyPolicyAccept")}
                    </Button>
                    <Button {...styles.closeButton} onClick={closeHandler}>
                        {translate("Close")}
                    </Button>
                </StyledWrapper>
            </StyledWrapper>
            {/* <--- Foundation End */}
        </Drawer>
    );
};

export default CookiePrivacyPolicyPopup;
